/**
 * 菜单本地图标映射。
 *
 * 映射键使用接口返回、国际化转换前的中文菜单标题，图标来自 UI 压缩包中的
 * assets/icons/ui-sprite.svg。现有菜单按照业务语义复用压缩包中的 37 个图标；
 * 未来新增且未配置映射的菜单，继续使用接口返回的 icon 和 activeIcon。
 */
export const localMenuIconMap: Readonly<Record<string, string>> = Object.freeze({
  // 首页与数据看板
  首页: 'menu-home',
  数据中心: 'menu-chart',
  客户看板: 'menu-grid',
  供应商看板: 'menu-org',
  会员数据: 'menu-users',

  // 客户与合作资源
  客户: 'menu-users',
  客户管理: 'menu-users',
  客户列表: 'menu-customer-list',
  供应商: 'menu-building',
  供应商列表: 'menu-list',
  子会员列表: 'menu-users',
  供应商等级: 'menu-chart',
  面板书: 'menu-file',
  生成面板书: 'menu-file',
  合作资源: 'menu-share',
  云客户: 'menu-users',
  云供应商: 'menu-building',
  我的: 'menu-user',
  合作租户: 'menu-link',

  // 项目
  项目: 'menu-folder',
  项目管理: 'menu-folder',
  项目列表: 'menu-list',
  混合API: 'menu-link',
  项目结算: 'menu-wallet',
  项目调度: 'menu-share',
  会员调度: 'menu-clock',

  // 记录
  记录管理: 'menu-file',
  财务: 'menu-wallet',
  记录: 'menu-file',
  参与记录: 'menu-kpi-participate',
  会员终止日志: 'menu-kpi-reject',
  调查记录: 'menu-list',
  操作日志: 'menu-clock',
  终止记录: 'menu-database',
  回调记录: 'menu-link',
  登录日志: 'menu-logout',

  // 财务
  财务管理: 'menu-wallet',
  供应商财务日志: 'menu-wallet',
  员工财务日志: 'menu-database',
  供应商账单: 'menu-file',
  供应商结算: 'menu-kpi-complete',
  员工结算: 'menu-kpi-complete',
  部门提成: 'menu-kpi-revenue',
  客户发票: 'menu-station',

  // 辅助功能
  辅助: 'menu-assist',
  辅助功能: 'menu-assist',
  文章公告: 'menu-file',
  汇率表: 'menu-wallet',
  发送消息: 'menu-share',
  通知中心: 'menu-bell',
  IP查询: 'menu-search',
  UID分拣: 'menu-grid',
  UID解析: 'menu-list',
  链路解析: 'menu-link',

  // 组织
  组织: 'menu-org',
  组织架构: 'menu-org',
  员工管理: 'menu-users',
  部门管理: 'menu-building',
  角色管理: 'menu-user',
  提成规则: 'menu-wallet',

  // 调查站
  调查站: 'menu-station',
  数据看板: 'menu-chart',
  数据分析: 'menu-database',
  调查系统: 'menu-station',
  仪表盘: 'menu-home',
  IR查询: 'menu-search',
  站点设置: 'menu-settings',
  会员管理: 'menu-user',
  会员组: 'menu-users',
  会员等级: 'menu-chart',
  会员财务日志: 'menu-wallet',
  会员账单: 'menu-wallet',
  员工账单: 'menu-user',
  财务日志: 'menu-wallet',
  账单管理: 'menu-file',
  会员: 'menu-user',
  管理: 'menu-settings',

  // 系统配置
  配置: 'menu-settings',
  配置管理: 'menu-settings',
  系统设置: 'menu-settings',
  数据字典: 'menu-database',
  前置问卷库: 'menu-file',
  站点模板: 'menu-box',
  三方配置: 'menu-share',
  短信配置: 'menu-bell',
  邮件配置: 'menu-bell',
  事件通知: 'menu-bell',
  等级管理: 'menu-sliders',
  安检配置: 'menu-shield',
  高级配置: 'menu-sliders',

  // 压缩包中的调查站菜单使用带“调查站”前缀的独立标题，必须优先精确匹配。
  调查站数据看板: 'menu-chart',
  调查站数据分析: 'menu-database',
  调查站项目管理: 'menu-folder',
  调查站项目调度: 'menu-clock',
  调查站调查记录: 'menu-file',
  调查站终止记录: 'menu-help',
  调查站会员管理: 'menu-user',
  调查站会员组: 'menu-users',
  调查站会员等级: 'menu-chart',
  调查站会员财务日志: 'menu-wallet',
  调查站员工财务日志: 'menu-file',
  调查站会员账单: 'menu-wallet',
  调查站员工账单: 'menu-user',
  调查站部门管理: 'menu-building',
  调查站角色管理: 'menu-user',
  调查站员工管理: 'menu-users',
})

/**
 * 同名菜单在不同业务模块中可能使用不同图标，因此组件路径映射优先于标题映射。
 * 例如普通“终止记录”使用 database，调查站“终止记录”使用 help。
 */
const localMenuIconByComponentMap: Readonly<Record<string, string>> = Object.freeze({
  'project/project_schedule/list.vue': 'menu-share',
  'record/project_record/list.vue': 'menu-list',
  'record/operation_logs/list.vue': 'menu-clock',
  'record/termination_record/list.vue': 'menu-database',
  'record/callback_record/list.vue': 'menu-link',
  'record/login_log/list.vue': 'menu-logout',
  'finance/employees_finance_logs/list.vue': 'menu-database',
  'survey/dashboard/dataBoard/index.vue': 'menu-chart',
  'survey/dashboard/dataAnalysis/index.vue': 'menu-database',
  'survey/project_manage/list.vue': 'menu-folder',
  'survey/scheduling/list.vue': 'menu-clock',
  'survey/survey_record/list.vue': 'menu-file',
  'survey/terminating_record/list.vue': 'menu-help',
  'survey/member_manage/list.vue': 'menu-user',
  'survey/group_manage/list.vue': 'menu-users',
  'survey/member_level/list.vue': 'menu-chart',
  'survey/finance/member_finance_logs/list.vue': 'menu-wallet',
  'survey/finance/employee_finance_logs/list.vue': 'menu-file',
  'survey/finance/member_bills/list.vue': 'menu-wallet',
  'survey/finance/employee_bills/list.vue': 'menu-user',
  'survey/department_manage/list.vue': 'menu-building',
  'survey/role_manage/list.vue': 'menu-user',
  'survey/employees_manage/list.vue': 'menu-users',
  'survey/survey_config/list.vue': 'menu-settings',
})

/**
 * 根据接口原始菜单标题查找本地图标。
 * 返回 undefined 表示没有匹配项，调用方需要保留接口返回的图标。
 */
export function resolveLocalMenuIcon(title: unknown, component?: unknown): string | undefined {
  if (typeof component === 'string') {
    const normalizedComponent = component.replaceAll('\\', '/').replace(/^\/+/, '')
    const componentIcon = Object.entries(localMenuIconByComponentMap)
      .find(([componentPath]) => normalizedComponent.endsWith(componentPath))?.[1]

    if (componentIcon) {
      return componentIcon
    }
  }

  return typeof title === 'string' ? localMenuIconMap[title] : undefined
}
